import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { PartnerDocsDialogLauncher } from '../../components/dispatch/PartnerDocsDialog';
import { PartnerDocItem, PartnerRole } from '../../components/dispatch/partnerDocRequirements';
import { PARTNER_ROLE_COLORS } from './ClientFilterBar';

/** A partner with a role on the case, as the feed composer's recipient row shows it. */
export interface CasePartnerChip {
  partnerId: string;
  role: PartnerRole;
  name: string;
  /** partners.email as stored (may be a comma-separated list); null = none on file. */
  email: string | null;
  /** What this partner is owed (partner-docs status, promptable items only). */
  missingDocs: PartnerDocItem[];
}

const ROLE_LABEL: Record<PartnerRole, string> = {
  crematorium: 'Crematorium',
  dropoff: 'Drop-off',
  transport: 'Transport',
};

/**
 * "Brampton Visitation Centre" → "Brampton VC", "Lakeshore Cardinal Crematorium
 * Inc." → "Lakeshore Cardinal". The chip shows the name only; the tooltip and
 * aria-label carry the role, full name and address.
 */
export function partnerShortName(name: string): string {
  const short = name
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\bVisitation Cent(re|er)\b/gi, 'VC')
    .replace(/[,\s]+(inc|ltd|limited|llc)\.?$/i, '')
    .replace(/\s+(crematorium|crematory|cremation services|funeral home)$/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  return short || name;
}

interface PartnerRecipientChipProps {
  partner: CasePartnerChip;
  funeralId: number;
  selected: boolean;
  onSelect: () => void;
}

/**
 * A partner in the composer's "To:" row. Rounded rectangle filled with the
 * ROLE colour (clients are pills in their own colours). Selecting it emails
 * that one partner. With no email on file it is disabled and says so, with a
 * link to add one. A missing-docs badge (separate hit target) opens the
 * partner-documents dialog with those documents pre-selected.
 */
export function PartnerRecipientChip({ partner, funeralId, selected, onSelect }: PartnerRecipientChipProps) {
  const [docsOpen, setDocsOpen] = useState(false);
  const color = PARTNER_ROLE_COLORS[partner.role];
  const role = ROLE_LABEL[partner.role];
  const hasEmail = !!partner.email?.trim();
  const missing = partner.missingDocs.length;
  const detail = `${role} · ${partner.name} · ${hasEmail ? partner.email : 'no email on file'}`;

  return (
    <span className="inline-flex shrink-0 snap-start items-center gap-1">
      <span
        className={cn(
          'inline-flex items-stretch overflow-hidden rounded-md border text-[11px] font-medium',
          selected && 'ring-2 ring-offset-1',
          !hasEmail && 'opacity-50',
        )}
        style={{ borderColor: color, ...(selected ? { ['--tw-ring-color' as any]: color } : {}) }}
      >
        <button
          type="button"
          disabled={!hasEmail}
          onClick={onSelect}
          title={detail}
          aria-label={`Email ${detail}`}
          aria-pressed={selected}
          className={cn('px-2 py-1 text-white', hasEmail ? 'cursor-pointer' : 'cursor-not-allowed')}
          style={{ backgroundColor: color }}
        >
          {partnerShortName(partner.name)}
        </button>
        {missing > 0 && (
          <button
            type="button"
            onClick={() => setDocsOpen(true)}
            title={`Send or chase: ${partner.missingDocs.map((d) => d.label.en).join(', ')}`}
            aria-label={`${missing} document${missing === 1 ? '' : 's'} missing for ${partner.name}`}
            className="border-l bg-white px-1.5 text-amber-700 hover:bg-amber-50"
            style={{ borderColor: color }}
          >
            · {missing} doc{missing === 1 ? '' : 's'} missing
          </button>
        )}
      </span>
      {!hasEmail && (
        <Link
          to={`/partners/${partner.partnerId}`}
          className="whitespace-nowrap text-[11px] text-muted-foreground underline-offset-2 hover:underline"
          title={`Add an email address for ${partner.name}`}
        >
          No email · add one
        </Link>
      )}
      {missing > 0 && (
        <PartnerDocsDialogLauncher
          open={docsOpen}
          onOpenChange={setDocsOpen}
          funeralId={funeralId}
          partnerId={partner.partnerId}
          documentTypes={partner.missingDocs.map((d) => d.documentType)}
        />
      )}
    </span>
  );
}
